import { smoothScrollToElement } from '../../utils/smoothScroll';
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Star, Play, MapPin, TrendingUp, Users, Award } from 'lucide-react';
import { testimonials } from '../../mock/mockData';

const TestimonialsSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('testimonials');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const scrollToForm = () => {
    const formSection = document.getElementById('contact-form');
    if (formSection) {
      smoothScrollToElement('contact-form', 350);
    }
  };

  return (
    <section 
      id="testimonials" 
      className="relative py-20 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(240,253,244,0.5) 0%, rgba(255,255,255,1) 50%, rgba(239,246,255,0.45) 100%)'
      }}
    >

      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-20 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"
             style={{backgroundColor: '#91d33d'}}></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"
             style={{backgroundColor: '#57c558', animationDelay: '3s'}}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Kundenstimmen – 
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-green-dark to-brand-green">
                Echte Erfahrungen
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Echte Erfahrungen von Familien aus Berlin & Brandenburg, die bereits auf Wärmepumpen umgestiegen sind.
            </p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-gray-200/50 hover:shadow-xl transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{
                  transitionDelay: `${200 + index * 100}ms`
                }}
              >
                {/* Video Placeholder */}
                <div className="aspect-[4/5] bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl mb-6 relative overflow-hidden group cursor-pointer">

                  
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 bg-gradient-to-r from-brand-green to-brand-green-hover rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-8 h-8 text-white ml-1" />
                      </div>
                      <div className="text-sm text-gray-600">
                        Video-Testimonial
                      </div>
                    </div>
                  </div>
                  
                  {/* Savings Badge */}
                  <div className="absolute top-4 right-4 bg-brand-orange text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {testimonial.savings}
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-4">
                  {/* Stars */}
                  <div className="flex space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="text-gray-700 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="flex items-center space-x-1 text-gray-600 text-sm">
                        <MapPin className="w-4 h-4" />
                        <span>{testimonial.location}</span>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Verifiziert</div>
                      <div className="flex space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 fill-brand-green text-brand-green" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Trust Stats */}
          <div className={`grid md:grid-cols-4 gap-6 mb-12 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center bg-white/70 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-200/50">
              <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">4.9</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Google Bewertung</h3>
              <p className="text-sm text-gray-600">Über 150 Bewertungen</p>
            </div>

            <div className="text-center bg-white/70 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-200/50">
              <div className="w-12 h-12 bg-gradient-to-r from-brand-green-dark to-brand-green rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">2.000+ Projekte</h3>
              <p className="text-sm text-gray-600">Erfolgreich abgeschlossen</p>
            </div>

            <div className="text-center bg-white/70 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-200/50">
              <div className="w-12 h-12 bg-gradient-to-r from-brand-green to-brand-green-hover rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">13+ Jahre</h3>
              <p className="text-sm text-gray-600">Erfahrung im Markt</p>
            </div>

            <div className="text-center bg-white/70 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-200/50">
              <div className="w-12 h-12 bg-gradient-to-r from-brand-green-light to-brand-green rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">100%</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Berlin & Brandenburg</h3>
              <p className="text-sm text-gray-600">Regional verwurzelt</p>
            </div>
          </div>

          {/* CTA */}
          <div className={`text-center transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-brand-orange hover:bg-brand-orange-hover text-white px-10 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Auch Sie können sparen – Jetzt Angebot erhalten
            </Button>
            <p className="text-gray-500 mt-4 text-sm">
              Kostenlose Beratung • Unverbindlich • Schnelle Rückmeldung
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;